import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight, ChevronLeft, AlertTriangle, UserCheck, X, CheckSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../App';
import ExpertVisitModal from '../components/ExpertVisitModal';

const CartPage = () => {
    const navigate = useNavigate();
    const { cart, removeFromCart, updateQuantity, cartTotal, cartCount } = useCart();
    const [showWarning, setShowWarning] = useState(false);
    const [showExpertModal, setShowExpertModal] = useState(false);
    const [dontShowAgain, setDontShowAgain] = useState(false);

    // Check localStorage for "don't show again" preference
    useEffect(() => {
        const hideWarning = localStorage.getItem('hideCartWarning');
        if (hideWarning === 'true') {
            setDontShowAgain(true);
        }
    }, []);

    const handleProceedClick = () => {
        if (dontShowAgain || localStorage.getItem('hideCartWarning') === 'true') {
            navigate('/checkout');
        } else {
            setShowWarning(true);
        }
    };

    const handleProceedAnyway = () => {
        if (dontShowAgain) {
            localStorage.setItem('hideCartWarning', 'true');
        }
        setShowWarning(false);
        navigate('/checkout');
    };

    const handleGetExpertVisit = () => {
        setShowWarning(false);
        setShowExpertModal(true);
    };

    if (cart.length === 0) {
        return (
            <div className="min-h-screen md:pt-20 flex items-center justify-center -mt-14 pt-14 md:mt-0">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
                    <ShoppingBag className="w-20 h-20 mx-auto text-muted-foreground/30 mb-6" />
                    <h1 className="text-2xl font-bold mb-2">Your cart is empty</h1>
                    <p className="text-muted-foreground mb-6">Looks like you haven't added any products yet.</p>
                    <Button className="btn-primary" onClick={() => navigate('/shop')}>Browse Products</Button>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen md:pt-20">
            <div className="container-solar -mt-14 pt-14 md:mt-0 md:pt-8">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                    <Link to="/shop" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-6"><ChevronLeft className="w-4 h-4" />Continue Shopping</Link>
                    <h1 className="text-2xl font-bold mb-8">Shopping Cart ({cartCount} items)</h1>
                </motion.div>

                <div className="grid lg:grid-cols-3 gap-8">
                    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="lg:col-span-2 space-y-4">
                        {cart.map((item, index) => (
                            <motion.div key={item.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="bg-card rounded-xl border p-4 md:p-6">
                                <div className="flex gap-4">
                                    <Link to={`/product/${item.id}`} className="shrink-0">
                                        <img src={item.image_url || 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=200'} alt={item.name} className="w-24 h-24 md:w-32 md:h-32 object-cover rounded-lg" />
                                    </Link>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between gap-4">
                                            <div><Link to={`/product/${item.id}`} className="font-semibold hover:text-primary line-clamp-2">{item.name}</Link>
                                                <p className="text-sm text-muted-foreground mt-1">{item.system_size_kw} kW • {item.brand}</p></div>
                                            <button onClick={() => removeFromCart(item.id)} className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="w-5 h-5" /></button>
                                        </div>
                                        <div className="flex flex-wrap items-end justify-between gap-4 mt-4">
                                            <div className="flex items-center border rounded-lg">
                                                <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="p-2 hover:bg-secondary"><Minus className="w-4 h-4" /></button>
                                                <span className="w-10 text-center font-medium">{item.quantity}</span>
                                                <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="p-2 hover:bg-secondary"><Plus className="w-4 h-4" /></button>
                                            </div>
                                            <p className="text-xl font-bold">₹{((item.price || 0) * (item.quantity || 1)).toLocaleString()}</p>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>

                    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="lg:col-span-1">
                        <div className="bg-card rounded-xl border p-6 sticky top-24">
                            <h2 className="font-semibold text-lg mb-6">Order Summary</h2>
                            <div className="space-y-4 text-sm">
                                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal ({cartCount} items)</span><span>₹{cartTotal.toLocaleString()}</span></div>
                                <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span className="text-accent">Free</span></div>
                                <div className="flex justify-between"><span className="text-muted-foreground">Tax (18% GST)</span><span>₹{Math.round(cartTotal * 0.18).toLocaleString()}</span></div>
                            </div>
                            <div className="border-t my-6" />
                            <div className="flex justify-between text-lg font-bold mb-6"><span>Total</span><span>₹{Math.round(cartTotal * 1.18).toLocaleString()}</span></div>
                            <Button onClick={handleProceedClick} className="w-full btn-primary">Proceed to Checkout<ArrowRight className="w-4 h-4 ml-2" /></Button>
                            <p className="text-xs text-center text-muted-foreground mt-4">Free delivery on all orders</p>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Warning Popup */}
            <AnimatePresence>
                {showWarning && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                        onClick={(e) => e.target === e.currentTarget && setShowWarning(false)}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="bg-card rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
                        >
                            {/* Header */}
                            <div className="bg-amber-500 p-5 text-white relative">
                                <button
                                    onClick={() => setShowWarning(false)}
                                    className="absolute top-3 right-3 p-1 hover:bg-white/20 rounded-full"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                                        <AlertTriangle className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold">Important Notice</h3>
                                        <p className="text-amber-100 text-sm">Before you proceed</p>
                                    </div>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-6 space-y-4">
                                <p className="text-foreground leading-relaxed">
                                    Buying solar panels is a <strong>big decision</strong>! To get the best results,
                                    you need to know the right system size based on your <strong>electricity usage</strong> and
                                    <strong> property type</strong>.
                                </p>

                                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                                    <p className="text-sm text-amber-800 dark:text-amber-200">
                                        💡 <strong>Tip:</strong> A solar expert can visit your home for
                                        <span className="text-amber-600 dark:text-amber-400 font-medium"> FREE</span>,
                                        check your roof, and recommend the perfect system for you!
                                    </p>
                                </div>

                                {/* Buttons */}
                                <div className="space-y-3 pt-2">
                                    <Button
                                        onClick={handleGetExpertVisit}
                                        className="w-full bg-primary hover:bg-primary/90 py-5"
                                    >
                                        <UserCheck className="w-5 h-5 mr-2" />
                                        Get Free Expert Visit
                                    </Button>

                                    <Button
                                        onClick={handleProceedAnyway}
                                        variant="outline"
                                        className="w-full py-5"
                                    >
                                        I Know What I Need - Proceed
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </div>

                                {/* Don't show again */}
                                <label className="flex items-center gap-2 cursor-pointer text-sm text-muted-foreground pt-2">
                                    <button
                                        onClick={() => setDontShowAgain(!dontShowAgain)}
                                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${dontShowAgain
                                            ? 'bg-primary border-primary text-white'
                                            : 'border-muted-foreground/50 hover:border-primary'
                                            }`}
                                    >
                                        {dontShowAgain && <CheckSquare className="w-4 h-4" />}
                                    </button>
                                    Don't show this message again
                                </label>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Expert Visit Modal */}
            <ExpertVisitModal
                isOpen={showExpertModal}
                onClose={() => setShowExpertModal(false)}
            />
        </div>
    );
};

export default CartPage;
